import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { Clock, ArrowLeft, X } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import './CourseDetails.css';

const CourseDetails = () => {
    const { lang } = useLanguage();
    const location = useLocation();
    const navigate = useNavigate();
    const course = location.state?.course;

    const [showForm, setShowForm] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [status, setStatus] = useState({ type: '', msg: '' });

    // 1. Translation
    const tForm = {
        en: {
            backBtn: "Back to All Courses",
            placeholderDesc: "Contact our administration for the complete course curriculum and learning objectives.",
            motivational: "Don't wait! Book this course today and start your journey towards mastering new skills.",
            bookBtn: "BOOK THIS COURSE",
            formTitle: "Course Inquiry",
            namePh: "Full Name",
            emailPh: "Email Address",
            phonePh: "Phone Number",
            submitBtn: "SEND REQUEST",
            successMsg: "Request sent! We will contact you soon.",
            errorMsg: "Error sending request.",
            durationInfo: "Duration: Approximately 1 week",
            defaultMsg: (title) => `I would like to enroll in the "${title}" course.`
        },
        jp: {
            backBtn: "全コースに戻る",
            placeholderDesc: "コースのカリキュラムと学習目標の詳細については、事務局までお問い合わせください。",
            motivational: "今すぐ受講予約をして、新しいスキルの習得に向けた第一歩を踏み出しましょう。",
            bookBtn: "このコースを予約する",
            formTitle: "受講のお申し込み",
            namePh: "氏名",
            emailPh: "メールアドレス",
            phonePh: "電話番号",
            submitBtn: "リクエストを送信",
            successMsg: "リクエストを送信しました！まもなくご連絡いたします。",
            errorMsg: "送信中にエラーが発生しました。",
            durationInfo: "期間：約1週間",
            defaultMsg: (title) => `「${title}」コースへの受講を希望します。`
        }
    };

    const currentT = tForm[lang];

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: '',
        hp_field: ''
    });

    useEffect(() => {
        if (course) {
            const courseTitle = lang === 'jp' && course.titleJp ? course.titleJp : course.title;
            setFormData(prev => ({
                ...prev,
                message: currentT.defaultMsg(courseTitle)
            }));
        }
    }, [course, lang]);

    if (!course) {
        return <Container className="py-5"><h3>Course not found.</h3><Button onClick={() => navigate('/courses')}>Back</Button></Container>;
    }

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleBooking = async (e) => {
        e.preventDefault();

        // Honeypot
        if (formData.hp_field !== "") return;

        setIsSubmitting(true);
        setStatus({ type: '', msg: '' });

        const payload = {
            courseTitle: lang === 'jp' && course.titleJp ? course.titleJp : course.title,
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            message: formData.message,
            hp_field: formData.hp_field
        };

        try {
            const [nodeResponse, phpResponse] = await Promise.allSettled([
                //fetch('http://localhost:5000/api-portal/course-requests', {
                fetch('/api-portal/course-requests', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                }),
                fetch('/course-request.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                })
            ]);

            const nodeSucceeded = nodeResponse.status === 'fulfilled' && nodeResponse.value.ok;

            if (nodeSucceeded) {
                setStatus({ type: 'success', msg: currentT.successMsg });
                setFormData({ ...formData, name: '', email: '', phone: '' });
            } else {
                throw new Error("Failed to process request");
            }

        } catch (error) {
            console.error("Enrollment error:", error);
            setStatus({ type: 'danger', msg: currentT.errorMsg });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="course-details-page">
            <Container className="py-5">
                <div className="mb-5">
                    <button className="back-to-grid-btn" onClick={() => navigate('/courses')}>
                        <ArrowLeft size={18} className="me-2" />
                        {currentT.backBtn}
                    </button>
                </div>

                <Row className="g-5 align-items-start">
                    <Col lg={5} className="text-center">
                        <div className="details-img-container">
                            <img
                                //src={course.image ? `http://localhost:5000/api-portal/uploads/courses/${course.image}` : '/placeholder.jpg'}
                                src={course.image ? `/api-portal/uploads/courses/${course.image}` : '/placeholder.jpg'}
                                alt={course.title}
                                className="details-course-img"
                            />
                        </div>
                    </Col>

                    <Col lg={7}>
                        <div className="details-content-box">
                            <h1 className="details-title">
                                {lang === 'jp' && course.titleJp ? course.titleJp : course.title}
                            </h1>
                            <div className="details-duration-badge">
                                <Clock size={18} className="me-2" /> {currentT.durationInfo}
                            </div>

                            <hr className="details-divider" />

                            <div className="details-description-area">
                                <p className="main-desc">
                                    {lang === 'jp'
                                        ? (course.descriptionJp || currentT.placeholderDesc)
                                        : (course.description || currentT.placeholderDesc)
                                    }
                                </p>
                            </div>

                            <hr className="details-divider" />

                            <div className="motivational-section">
                                <p className="motivational-text">
                                    {currentT.motivational}
                                </p>
                            </div>

                            {!showForm ? (
                                <Button className="enroll-btn-sharp mt-4" onClick={() => setShowForm(true)}>
                                    {currentT.bookBtn}
                                </Button>
                            ) : (
                                <div className="booking-form-wrapper animate-in mt-4">
                                    <button className="close-form-btn" onClick={() => setShowForm(false)}>
                                        <X size={20} />
                                    </button>

                                    <h4 className="mb-4">{currentT.formTitle}</h4>
                                    {status.msg && <Alert variant={status.type} className="sharp-alert">{status.msg}</Alert>}

                                    <Form onSubmit={handleBooking}>
                                        <div style={{ display: 'none' }}>
                                            <input name="hp_field" value={formData.hp_field} onChange={handleInputChange} />
                                        </div>
                                        <Row>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Control
                                                        name="name" required placeholder={currentT.namePh}
                                                        className="sharp-input" value={formData.name} onChange={handleInputChange}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Control
                                                        type="email" name="email" required placeholder={currentT.emailPh}
                                                        className="sharp-input" value={formData.email} onChange={handleInputChange}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Form.Group className="mb-3">
                                            <Form.Control
                                                name="phone" required placeholder={currentT.phonePh}
                                                className="sharp-input" value={formData.phone} onChange={handleInputChange}
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-4">
                                            <Form.Control
                                                as="textarea" rows={3} name="message"
                                                className="sharp-input" value={formData.message} onChange={handleInputChange}
                                            />
                                        </Form.Group>
                                        <Button type="submit" className="submit-booking-btn" disabled={isSubmitting}>
                                            {isSubmitting ? '...' : currentT.submitBtn}
                                        </Button>
                                    </Form>
                                </div>
                            )}
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default CourseDetails;